import type { ImpersonationTargetDto, NotificationDto, Resource, SearchResultDto } from '@ukinfo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ApiError, api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useAppStartCode } from '../lib/app-start';
import { ClubLogo, useClub, useFavicon } from '../lib/club';
import { useScope } from '../lib/scope';
import { useOfflineAutoSync } from '../lib/offline';
import { offlineSupported } from '../lib/offline-store';
import { setImageBrand } from '../lib/share-image';
import { relativeDays } from '../lib/format';
import { Badge, Button, cn, Dialog, Spinner, useScrollLock, useToast } from './ui';
import { OfflineBanner } from './OfflineBanner';
import { OrgUnitPicker } from './OrgUnitPicker';

interface NavEintrag {
  to: string;
  label: string;
  icon: string;
  resources?: readonly Resource[];
  /** Untereintraege klappen auf; der Hauptpunkt selbst bleibt anklickbar. */
  kinder?: readonly { to: string; label: string; resources?: readonly Resource[] }[];
  /** Die eigenen Mannschaften stehen erst beim Anmelden fest. */
  mannschaften?: true;
}

/**
 * Hauptnavigation: fuenf Punkte.
 *
 * Alles, was taeglich gebraucht wird, steht oben; der Rest liegt unter
 * "Verein". "Mannschaften" klappt die eigenen Mannschaften auf, der Punkt
 * selbst fuehrt auf die Uebersicht mit Saison, Planung und Auswertungen.
 */
const SPORT_RECHTE: readonly Resource[] = ['matches', 'sports_participation', 'sports_attendance', 'sports_reports'];

const NAV: readonly NavEintrag[] = [
  { to: '/heute', label: 'Heute', icon: 'home' },
  {
    to: '/aufgaben',
    label: 'Aufgaben',
    icon: 'check',
    resources: ['tasks'],
    // Umfragen sind Aufgaben an andere - sie stehen deshalb beisammen.
    kinder: [{ to: '/umfragen', label: 'Umfragen' }],
  },
  { to: '/kalender', label: 'Kalender', icon: 'calendar', resources: ['calendar'] },
  { to: '/sport', label: 'Mannschaften', icon: 'users', resources: SPORT_RECHTE, mannschaften: true },
  {
    to: '/verein',
    label: 'Verein',
    icon: 'building',
    kinder: [
      { to: '/mitglieder', label: 'Mitglieder', resources: ['persons'] },
      { to: '/sponsoring', label: 'Sponsoring', resources: ['sponsoring'] },
      { to: '/medien', label: 'Medien', resources: ['media'] },
      { to: '/wissen', label: 'Wissen', resources: ['documents'] },
      { to: '/finanzen', label: 'Finanzen', resources: ['finance'] },
      { to: '/verein', label: 'Struktur und Verwaltung', resources: ['organisation', 'permissions'] },
    ],
  },
];

const ICONS: Record<string, string> = {
  home: 'M2.25 12l8.954-8.955a1.126 1.126 0 011.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75',
  poll: 'M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z',
  check: 'M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  calendar:
    'M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5',
  handshake:
    'M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25',
  image:
    'M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M18 6h.008v.008H18V6zm2.25 12H3.75A1.5 1.5 0 012.25 16.5v-9A1.5 1.5 0 013.75 6h16.5a1.5 1.5 0 011.5 1.5v9a1.5 1.5 0 01-1.5 1.5z',
  book: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253',
  users:
    'M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z',
  bell: 'M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0',
  search: 'M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z',
  layers:
    'M6.429 9.75L2.25 12l4.179 2.25m0-4.5l5.571 3 5.571-3m-11.142 0L2.25 7.5 12 2.25l9.75 5.25-4.179 2.25m0 0L21.75 12l-4.179 2.25m0 0l4.179 2.25L12 21.75 2.25 16.5l4.179-2.25m11.142 0l-5.571 3-5.571-3',
  menu: 'M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5',
  settings:
    'M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a6.759 6.759 0 010 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 010-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.281z',
  logout: 'M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75',
  eye: 'M2.25 12s3.75-6.75 9.75-6.75S21.75 12 21.75 12 18 18.75 12 18.75 2.25 12 2.25 12zm9.75 2.25a2.25 2.25 0 100-4.5 2.25 2.25 0 000 4.5z',
  chevron: 'M8.25 4.5l7.5 7.5-7.5 7.5',
  ball: 'M12 21a9 9 0 100-18 9 9 0 000 18zm0 0V3m9 9H3m2.6-6.4l12.8 12.8m0-12.8L5.6 18.4',
  building:
    'M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21',
};

function Icon({ name, className }: { name: string; className?: string }): JSX.Element {
  return (
    <svg
      className={className ?? 'h-5 w-5'}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={ICONS[name] ?? ICONS['home'] ?? ''} />
    </svg>
  );
}

/* ------------------------------------------------------- Bereichsumschalter */

/**
 * Zentraler Bereichsumschalter.
 * Er ist bewusst beschriftet, damit sofort erkennbar ist, wofuer er dient:
 * Er begrenzt alle Inhalte der Anwendung auf den gewaehlten Vereinsbereich.
 */
function ScopeSwitcher({ compact = false }: { compact?: boolean }): JSX.Element {
  const scope = useScope();
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Bereich wechseln – begrenzt alle Inhalte auf diesen Vereinsbereich"
        className={cn(
          'group flex max-w-full items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-left transition-colors hover:border-brand-300 hover:bg-brand-50/40',
          compact ? 'py-1.5' : 'w-full py-2',
        )}
      >
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-brand-100 text-brand-700">
          <Icon name="layers" className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400">
            Bereich
          </span>
          <span className="block truncate text-sm font-medium text-slate-800">
            {scope.breadcrumb}
          </span>
        </span>
        <svg
          className="h-4 w-4 shrink-0 text-slate-400 transition-transform group-hover:text-brand-500"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" />
        </svg>
      </button>

      <Dialog open={open} onClose={() => setOpen(false)} title="Bereich wechseln">
        <p className="mb-3 text-sm text-slate-600">
          Die Auswahl bestimmt, welche Aufgaben, Termine, Personen, Dokumente, Medien und
          Sponsoren angezeigt werden.
        </p>
        <button
          type="button"
          onClick={() => {
            scope.setUnitId(null);
            setOpen(false);
          }}
          className={cn(
            'mb-2 w-full rounded-xl border px-3 py-2.5 text-left text-sm font-medium transition-colors',
            scope.unitId === null
              ? 'border-brand-400 bg-brand-50 text-brand-800'
              : 'border-slate-200 hover:bg-slate-50',
          )}
        >
          Gesamtverein
          <span className="ml-2 text-xs font-normal text-slate-500">alle Bereiche</span>
        </button>
        <OrgUnitPicker
          value={scope.unitId ? [scope.unitId] : []}
          multiple={false}
          onChange={(ids) => {
            scope.setUnitId(ids[0] ?? null);
            setOpen(false);
          }}
        />
        <label className="mt-3 flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={scope.includeChildren}
            onChange={(e) => scope.setIncludeChildren(e.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-brand-600"
          />
          Untergeordnete Bereiche einbeziehen
        </label>
      </Dialog>
    </>
  );
}

/* --------------------------------------------------------------- Globale Suche */

function GlobalSearch(): JSX.Element {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(term), 250);
    return () => window.clearTimeout(timer);
  }, [term]);

  useEffect(() => {
    if (open) window.setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  const { data, isFetching } = useQuery({
    queryKey: ['search', debounced],
    queryFn: () => api.get<SearchResultDto[]>('/api/search', { q: debounced }),
    enabled: debounced.trim().length >= 2,
  });

  const KIND_LABELS: Record<string, string> = {
    person: 'Person',
    task: 'Aufgabe',
    event: 'Termin',
    match: 'Spiel',
    sponsor: 'Sponsor',
    document: 'Dokument',
    media: 'Medium',
    organisationUnit: 'Bereich',
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Suchen"
        className="rounded-xl p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
      >
        <Icon name="search" />
      </button>

      <Dialog open={open} onClose={() => setOpen(false)} title="Suche" wide>
        <input
          ref={inputRef}
          className="input"
          placeholder="Personen, Aufgaben, Termine, Sponsoren, Dokumente …"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
        />
        <div className="mt-3">
          {term.trim().length < 2 && (
            <p className="py-6 text-center text-sm text-slate-500">
              Mindestens zwei Zeichen eingeben.
            </p>
          )}
          {isFetching && <Spinner className="mx-auto my-6 h-5 w-5 text-slate-400" />}
          {data && data.length === 0 && term.trim().length >= 2 && !isFetching && (
            <p className="py-6 text-center text-sm text-slate-500">Nichts gefunden.</p>
          )}
          <ul className="divide-y divide-slate-100">
            {(data ?? []).map((result) => (
              <li key={`${result.kind}-${result.id}`}>
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-3 rounded-lg px-2 py-2.5 text-left hover:bg-slate-50"
                  onClick={() => {
                    setOpen(false);
                    setTerm('');
                    navigate(result.link);
                  }}
                >
                  <span className="min-w-0">
                    <span className="block truncate font-medium text-slate-800">
                      {result.title}
                    </span>
                    {result.subtitle && (
                      <span className="block truncate text-xs text-slate-500">
                        {result.subtitle}
                      </span>
                    )}
                  </span>
                  <Badge tone="neutral">{KIND_LABELS[result.kind] ?? result.kind}</Badge>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </Dialog>
    </>
  );
}

/* ------------------------------------------------------------ Benachrichtigungen */

function NotificationBell(): JSX.Element {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.get<{ items: NotificationDto[]; unread: number }>('/api/notifications'),
    refetchInterval: 60_000,
  });

  const markRead = useMutation({
    mutationFn: (ids: string[] | 'all') =>
      api.post('/api/notifications/read', ids === 'all' ? { all: true } : { ids }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
    onError: (error: unknown) =>
      toast.show(error instanceof ApiError ? error.message : 'Als gelesen markieren fehlgeschlagen', 'error'),
  });

  const unread = data?.unread ?? 0;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`Benachrichtigungen${unread > 0 ? ` (${unread} ungelesen)` : ''}`}
        className="relative rounded-xl p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700"
      >
        <Icon name="bell" />
        {unread > 0 && (
          <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-semibold text-white ring-2 ring-white">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        title="Benachrichtigungen"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setOpen(false);
                navigate('/einstellungen?reiter=benachrichtigungen');
              }}
            >
              Einstellungen
            </Button>
            <Button
              onClick={() => markRead.mutate('all')}
              loading={markRead.isPending}
              disabled={unread === 0}
            >
              Alle gelesen
            </Button>
          </>
        }
      >
        {(data?.items ?? []).length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-500">Keine Meldungen vorhanden.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {(data?.items ?? []).map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  className={cn(
                    'w-full rounded-lg px-2 py-3 text-left hover:bg-slate-50',
                    item.readAt === null && 'bg-brand-50/60',
                  )}
                  onClick={() => {
                    markRead.mutate([item.id]);
                    if (item.link) {
                      setOpen(false);
                      navigate(item.link);
                    }
                  }}
                >
                  <span className="flex items-start justify-between gap-2">
                    <span className="font-medium text-slate-800">{item.title}</span>
                    <span className="shrink-0 text-xs text-slate-400">
                      {relativeDays(item.createdAt)}
                    </span>
                  </span>
                  {item.body && (
                    <span className="mt-0.5 block text-sm text-slate-600">{item.body}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </Dialog>
    </>
  );
}

/* ------------------------------------------------------------------ Sidebar */

function ViewAsButton(): JSX.Element | null {
  const { user, viewAs } = useAuth();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [pending, setPending] = useState<string | null>(null);
  const toast = useToast();
  const { data, isLoading } = useQuery({
    queryKey: ['auth', 'view-as-targets'],
    queryFn: () => api.get<ImpersonationTargetDto[]>('/api/auth/view-as/targets'),
    enabled: open && Boolean(user?.canImpersonate),
  });

  if (!user?.canImpersonate || user.isImpersonating) return null;
  const term = search.trim().toLocaleLowerCase('de');
  const targets = (data ?? []).filter(
    (target) =>
      target.personId !== user.personId &&
      (!term || `${target.displayName} ${target.email} ${target.roleNames.join(' ')}`.toLocaleLowerCase('de').includes(term)),
  );

  return (
    <>
      <Button variant="secondary" size="sm" className="mb-2 w-full" onClick={() => setOpen(true)}>
        <Icon name="eye" className="h-4 w-4" />
        Ansicht als …
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} title="Ansicht als Benutzer testen">
        <p className="mb-3 text-sm text-slate-600">
          Die Oberfläche und alle Berechtigungen verhalten sich wie beim gewählten Zugang.
        </p>
        <input
          className="input mb-3"
          placeholder="Name, E-Mail oder Funktion suchen …"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        {isLoading ? (
          <Spinner className="mx-auto my-6 h-5 w-5 text-slate-400" />
        ) : (
          <ul className="max-h-80 divide-y divide-slate-100 overflow-y-auto">
            {targets.map((target) => (
              <li key={target.personId}>
                <button
                  type="button"
                  disabled={pending !== null}
                  className="w-full rounded-lg px-2 py-3 text-left hover:bg-slate-50 disabled:opacity-50"
                  onClick={async () => {
                    setPending(target.personId);
                    try {
                      await viewAs(target.personId);
                      setOpen(false);
                    } catch (error) {
                      toast.show(error instanceof Error ? error.message : 'Testansicht konnte nicht gestartet werden', 'error');
                    } finally {
                      setPending(null);
                    }
                  }}
                >
                  <span className="block font-medium text-slate-800">{target.displayName}</span>
                  <span className="block text-xs text-slate-500">{target.email}</span>
                  {target.roleNames.length > 0 && (
                    <span className="mt-1 block text-xs text-slate-400">{target.roleNames.join(' · ')}</span>
                  )}
                </button>
              </li>
            ))}
            {targets.length === 0 && (
              <li className="py-6 text-center text-sm text-slate-500">Kein passender Zugang.</li>
            )}
          </ul>
        )}
      </Dialog>
    </>
  );
}

/**
 * Ein Punkt der Hauptnavigation - bei Bedarf mit Untereintraegen.
 *
 * Der Hauptpunkt bleibt immer ein Link: "Mannschaften" fuehrt auf die
 * Uebersicht mit Saison und Planung, die aufgeklappte Liste direkt in eine
 * Mannschaft. Aufgeklappt ist, was zum aktuellen Pfad gehoert.
 */
function NavPunkt({ item, onNavigate }: { item: NavEintrag; onNavigate?: () => void }): JSX.Element {
  const { can } = useAuth();
  const location = useLocation();
  const teams = useMeineMannschaften(item.mannschaften === true);
  const kinder = item.mannschaften
    ? [
        ...teams.map((team) => ({ to: `/sport/${team.id}`, label: team.name })),
        { to: '/sport', label: 'Alle Mannschaften' },
        // Trainingsplanung (Test): Einstieg nur mit Leserecht.
        ...(can('sports_training_plans', 'read')
          ? [
              { to: '/sport/uebungen', label: 'Übungen' },
              { to: '/sport/einheiten', label: 'Einheiten' },
              { to: '/sport/formationen', label: 'Formationen' },
              // Für den Platz ohne Netz.
              ...(offlineSupported() ? [{ to: '/offline', label: 'Offline gespeichert' }] : []),
            ]
          : []),
      ]
    : (item.kinder ?? []).filter((kind) => !kind.resources || kind.resources.some((resource) => can(resource, 'read')));
  const gehoertDazu = kinder.some((kind) => location.pathname.startsWith(kind.to)) || location.pathname.startsWith(item.to);
  const [offen, setOffen] = useState(gehoertDazu);

  return (
    <div>
      <div className="flex items-stretch gap-1">
        <NavLink
          to={item.to}
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              'group flex flex-1 items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors',
              isActive
                ? 'bg-brand-600 text-white shadow-sm'
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
            )
          }
        >
          {({ isActive }) => (
            <>
              <Icon name={item.icon} className={cn('h-5 w-5', isActive ? 'text-white' : 'text-slate-400')} />
              {item.label}
            </>
          )}
        </NavLink>
        {kinder.length > 0 && (
          <button
            type="button"
            aria-expanded={offen}
            aria-label={offen ? `${item.label} zuklappen` : `${item.label} aufklappen`}
            onClick={() => setOffen(!offen)}
            className="rounded-xl px-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
          >
            <Icon name="chevron" className={cn('h-4 w-4 transition-transform', offen && 'rotate-90')} />
          </button>
        )}
      </div>

      {offen && kinder.length > 0 && (
        <ul className="ml-6 mt-0.5 space-y-0.5 border-l border-slate-200 pl-3">
          {kinder.map((kind) => (
            <li key={`${kind.to}-${kind.label}`}>
              <NavLink
                to={kind.to}
                end
                onClick={onNavigate}
                className={({ isActive }) =>
                  cn(
                    'block rounded-lg px-3 py-1.5 text-sm transition-colors',
                    isActive ? 'bg-brand-50 font-medium text-brand-700' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
                  )
                }
              >
                {kind.label}
              </NavLink>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Die eigenen Mannschaften fuer die Navigation: hoechstens drei, die mit dem
 * naechsten Termin zuerst. Mehr waere im Menue eine zweite Liste.
 */
function useMeineMannschaften(aktiv: boolean): { id: string; name: string }[] {
  const seasons = useQuery({
    queryKey: ['sports-seasons'],
    queryFn: () => api.get<{ id: string }[]>('/api/sports/seasons'),
    enabled: aktiv,
  });
  const seasonId = seasons.data?.[0]?.id ?? '';
  const teams = useQuery({
    queryKey: ['sports-teams', 'navigation', seasonId],
    queryFn: () => api.get<{ id: string; name: string; nextSession?: { startAt: string } | null }[]>('/api/sports/teams', { seasonId }),
    enabled: aktiv && Boolean(seasonId),
  });
  return [...(teams.data ?? [])]
    .sort((a, b) => (a.nextSession?.startAt ?? '9999').localeCompare(b.nextSession?.startAt ?? '9999'))
    .slice(0, 3)
    .map((team) => ({ id: team.id, name: team.name }));
}

function SidebarContent({ onNavigate }: { onNavigate?: () => void }): JSX.Element {
  const { user, logout, can } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async (): Promise<void> => {
    await logout();
    navigate('/anmelden');
  };

  const club = useClub();
  useFavicon(club);
  // Name unten auf Bildern (Skizze als Bild), solange der Verein bekannt ist.
  useEffect(() => setImageBrand(club?.name), [club?.name]);

  const initials = (user?.displayName ?? '')
    .split(' ')
    .map((part) => part[0])
    .slice(0, 2)
    .join('');

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2.5 px-4 py-4">
        <Link to="/heute" onClick={onNavigate} className="flex items-center gap-2.5">
          <ClubLogo club={club} size={36} />
          <span className="min-w-0">
            <span className="block truncate text-base font-semibold leading-tight text-slate-900">
              {club?.name ?? 'UK Info'}
            </span>
            <span className="block text-[11px] leading-tight text-slate-500">
              Vereinsplattform
            </span>
          </span>
        </Link>
      </div>

      {/* min-h-0: sonst draengt die Navigationsliste den Benutzerbereich
          am unteren Rand aus dem sichtbaren Bereich. */}
      <nav className="min-h-0 flex-1 space-y-0.5 overflow-y-auto px-3 py-2">
        {NAV.flatMap((item) => {
          // Umfragen stehen bei den Aufgaben. Wer keine Aufgaben fuehrt, sieht
          // sie trotzdem - gefragt werden kann jede Person.
          if (item.to === '/aufgaben' && !can('tasks', 'read')) {
            return [{ to: '/umfragen', label: 'Umfragen', icon: 'poll' } as NavEintrag];
          }
          const sichtbar = !item.resources || item.resources.some((resource) => can(resource, 'read'));
          return sichtbar ? [item] : [];
        }).map((item) => (
          <NavPunkt key={item.to} item={item} onNavigate={onNavigate} />
        ))}
      </nav>

      <div className="shrink-0 border-t border-slate-200 p-3">
        <ViewAsButton />
        <NavLink
          to="/einstellungen"
          onClick={onNavigate}
          className={({ isActive }) =>
            cn(
              'mb-2 flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors',
              isActive
                ? 'bg-brand-50 text-brand-700'
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
            )
          }
        >
          <Icon name="settings" className="h-5 w-5 text-slate-400" />
          Einstellungen
        </NavLink>

        <div className="rounded-xl bg-slate-50 p-3">
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-100 text-sm font-semibold text-brand-800">
              {initials}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium text-slate-900">
                {user?.displayName}
              </span>
              <span className="block truncate text-xs text-slate-500">{user?.email}</span>
            </span>
          </div>
          {(user?.roles ?? []).filter((r) => r.isCurrent).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {(user?.roles ?? [])
                .filter((role) => role.isCurrent)
                .slice(0, 3)
                .map((role) => (
                  <Badge key={role.id} tone="brand">
                    {role.roleName}
                  </Badge>
                ))}
            </div>
          )}
          {user?.isImpersonating && <Badge tone="warning">Testansicht</Badge>}
          <Button variant="secondary" size="sm" className="mt-3 w-full" onClick={handleLogout}>
            <Icon name="logout" className="h-4 w-4" />
            Abmelden
          </Button>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------- Shell */

export function AppShell({ children }: { children: ReactNode }): JSX.Element {
  const { user, stopViewingAs } = useAuth();
  const location = useLocation();
  // iPhone: Anmeldung beim Ablegen auf den Home-Bildschirm mitgeben.
  useAppStartCode(user);
  // Konto fuer den Offline-Speicher merken und die Trainings der naechsten 48 Stunden auffrischen.
  useOfflineAutoSync();
  const toast = useToast();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [stoppingView, setStoppingView] = useState(false);

  // Hintergrund sperren, solange das Menue am Smartphone geoeffnet ist.
  useScrollLock(drawerOpen);

  // Beim Seitenwechsel schliesst sich das Menue am Smartphone automatisch.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!drawerOpen) return;
    const handler = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setDrawerOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [drawerOpen]);

  // Auch die Untereintraege benennen ihre Seite - sonst bliebe die Kopfzeile
  // auf "Mitglieder" oder "Sponsoring" leer.
  const alleSeiten = NAV.flatMap((item) => [item, ...(item.kinder ?? [])]);
  const currentPage = location.pathname.startsWith('/verein')
    ? { label: 'Verein · Struktur und Verwaltung' }
    : [...alleSeiten].sort((a, b) => b.to.length - a.to.length)
      .find((item) => location.pathname.startsWith(item.to));

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Dauerhafte Navigationsleiste links */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 border-r border-slate-200 bg-white lg:block">
        <SidebarContent />
      </aside>

      {/* Ausklappbares Menue am Smartphone */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-slate-900/40"
            onClick={() => setDrawerOpen(false)}
            role="presentation"
          />
          <aside className="animate-fade absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-white shadow-xl">
            <SidebarContent onNavigate={() => setDrawerOpen(false)} />
          </aside>
        </div>
      )}

      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/90 backdrop-blur">
          <OfflineBanner />
          {user?.isImpersonating && (
            <div className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900">
              <div className="mx-auto flex max-w-5xl items-center justify-between gap-3">
                <span>
                  Testansicht als <strong>{user.displayName}</strong>
                  {user.actualDisplayName ? ` · angemeldet als ${user.actualDisplayName}` : ''}
                </span>
                <Button
                  size="sm"
                  variant="secondary"
                  loading={stoppingView}
                  onClick={async () => {
                    setStoppingView(true);
                    try {
                      await stopViewingAs();
                    } catch (error) {
                      toast.show(error instanceof Error ? error.message : 'Testansicht konnte nicht beendet werden', 'error');
                    } finally {
                      setStoppingView(false);
                    }
                  }}
                >
                  Testansicht beenden
                </Button>
              </div>
            </div>
          )}
          <div className="mx-auto flex max-w-5xl items-center gap-2 px-4 py-2.5">
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              aria-label="Menü öffnen"
              className="rounded-xl p-2 text-slate-600 transition-colors hover:bg-slate-100 lg:hidden"
            >
              <Icon name="menu" className="h-6 w-6" />
            </button>

            <span className="hidden text-sm font-semibold text-slate-800 lg:block">
              {currentPage?.label ?? 'UK Info'}
            </span>

            <div className="min-w-0 flex-1 lg:max-w-xs">
              <ScopeSwitcher compact />
            </div>

            <GlobalSearch />
            <NotificationBell />
          </div>
        </header>

        <main className="mx-auto max-w-5xl px-4 pb-16 pt-5">{children}</main>

        {user && (
          <footer className="mx-auto max-w-5xl px-4 pb-8 text-center text-xs text-slate-400">
            UK Info · angemeldet als {user.displayName}
          </footer>
        )}
      </div>
    </div>
  );
}
